/**
 * Outsource partner directory — keyed by `${category}::${product}` so the
 * Custom Order modal can show only the relevant suppliers when an admin
 * picks a product.
 *
 * Mirrors the operator-supplied partner sheet (iter62). Update via PR
 * when partners change — there is no admin UI for editing this yet
 * because (a) the list is small and (b) the email addresses are
 * production-critical and need code-review on every change.
 *
 * Helper `getPartnersForProduct(category, product)` returns an array
 * (possibly empty). The Custom Order form falls back to a free-text
 * partner field when no entry exists, so adding a brand-new product
 * never blocks the admin.
 */

const P = (name, email) => ({ name, email });

// Convenience aliases — most products inherit one of these supplier sets.
const DECALS_DEFAULT = [
  P('Westend Offset', 'Westendoffset@on.aibn.com'),
  P('Sign Mogul', 'saif@signmogul.com'),
];

const STICKERS_DEFAULT = [
  P('Westend Offset', 'Westendoffset@on.aibn.com'),
  P('Sign Mogul', 'saif@signmogul.com'),
];

const ROLL_LABELS_FULL = [
  P('Sinalite', 'Ian.buzon@sinalite.com'),
  P('Factor Forms', 'NatalieG@factorforms.com'),
  P('Cambridgelabel', 'elizabeth@cambridgelabel.com'),
  P('Royal Label', 'quote@royallabelprinting.com'),
  P('Digital Labels', 'Chris@digitallabels.ca'),
];

const SPECIALTY_DIGITAL_LABELS = [
  P('Digital Labels', 'chris@digitallabels.ca'),
];

const FALCON_ONLY = [
  P('Falcon Transfers', 'contact@falcontransfers.ca'),
];

const CALIFORNIA_TATTOOS = [
  P('California Tattoos', 'Sales@californiatattoos.com'),
];

export const PARTNERS_BY_PRODUCT = {
  // ── Category: Decals & Clings ─────────────────────────────────────
  'Decals & Clings::Clear Decals': DECALS_DEFAULT,
  'Decals & Clings::Indoor Floor Decals': DECALS_DEFAULT,
  'Decals & Clings::Static Cling': DECALS_DEFAULT,
  'Decals & Clings::Street Decals': DECALS_DEFAULT,
  'Decals & Clings::Vinyl Graphics': DECALS_DEFAULT,
  'Decals & Clings::Wall Decals': DECALS_DEFAULT,

  // ── Category: Labels ──────────────────────────────────────────────
  'Labels::White BOPP (Rolls)': ROLL_LABELS_FULL,
  'Labels::Clear BOPP (Rolls)': ROLL_LABELS_FULL,
  'Labels::Gold & Silver Foil (Rolls)': [P('Sinalite', 'Ian.buzon@sinalite.com')],
  'Labels::SemiGloss Paper (Rolls)': ROLL_LABELS_FULL,
  'Labels::White Vinyl (Pages)': DECALS_DEFAULT, // Page format → outsource fits the decal supplier set
  'Labels::Writable BOPP (Rolls)': ROLL_LABELS_FULL,

  // ── Category: Stickers ────────────────────────────────────────────
  'Stickers::Custom Stickers': STICKERS_DEFAULT,
  'Stickers::Die-Cut Sticker Singles': STICKERS_DEFAULT,
  'Stickers::Glitter Stickers': STICKERS_DEFAULT,
  'Stickers::Holographic Stickers': STICKERS_DEFAULT,
  'Stickers::Permanent Stickers': STICKERS_DEFAULT,
  'Stickers::Transparent Stickers': STICKERS_DEFAULT,

  // ── Category: Wearables ───────────────────────────────────────────
  'Wearables::Classic Tattoos': CALIFORNIA_TATTOOS,
  'Wearables::DTF Heat Transfers': FALCON_ONLY,
  'Wearables::DTF Heat Transfers (By Sheet)': FALCON_ONLY,
  'Wearables::Emblems (Patches)': [
    P('Falcon Transfers', 'contact@falcontransfers.ca'),
    P('Emblemtek', 'connie.dietrich@emblemtek.com'),
  ],
  'Wearables::Name Badges': [P('Westend Offset', 'Westendoffset@on.aibn.com')],
  'Wearables::Tattoo Booklets': CALIFORNIA_TATTOOS,

  // ── Category: Specialty Niche Products ────────────────────────────
  'Specialty Niche Products::Back Print': SPECIALTY_DIGITAL_LABELS,
  'Specialty Niche Products::Colour Changing Ink': SPECIALTY_DIGITAL_LABELS,
  'Specialty Niche Products::Embossing': SPECIALTY_DIGITAL_LABELS,
  'Specialty Niche Products::Hot Stamping': SPECIALTY_DIGITAL_LABELS,
  "Specialty Niche Products::Scratch 'N Sniff": SPECIALTY_DIGITAL_LABELS,
  'Specialty Niche Products::Silkscreen Effects': SPECIALTY_DIGITAL_LABELS,

  // ── Category: Unique Custom Products ──────────────────────────────
  'Unique Custom Products::Glow In The Dark': [
    P('Sign Mogul', 'saif@signmogul.com'),
    P('Digital Labels', 'chris@digitallabels.ca'),
  ],
  'Unique Custom Products::Magnets': [
    P('Westend Offset', 'Westendoffset@on.aibn.com'),
    P('XL Print', 'printing@xlprints.ca'),
  ],
  'Unique Custom Products::Multi-Ply Labels': SPECIALTY_DIGITAL_LABELS,
  'Unique Custom Products::Packing Tape': [
    P('Royal Label', 'quote@royallabelprinting.com'),
    P('Discount Labels', 'quotes@discountlabels.com'),
  ],
  'Unique Custom Products::UV DTF Stickers': FALCON_ONLY,
  'Unique Custom Products::Vehicle Magnets': [
    P('Westend Offset', 'Westendoffset@on.aibn.com'),
    P('XL Print', 'printing@xlprints.ca'),
  ],
};

/**
 * Return the outsource-partner list for a given category + product, or
 * an empty array if the combo isn't mapped (the form will surface a
 * free-text fallback so adding a new product never blocks the admin).
 */
export const getPartnersForProduct = (category, product) => {
  if (!category || !product) return [];
  return PARTNERS_BY_PRODUCT[`${category}::${product}`] || [];
};

/**
 * Sorted list of every distinct (category, product) tuple — used to
 * populate the Category + Product selectors in the modal. Computed
 * once at module load (cheap; ~50 entries).
 */
export const ALL_PRODUCTS = Object.keys(PARTNERS_BY_PRODUCT)
  .map((key) => {
    const [category, product] = key.split('::');
    return { category, product };
  })
  .sort((a, b) =>
    a.category.localeCompare(b.category) || a.product.localeCompare(b.product),
  );

/**
 * Distinct categories — derived from ALL_PRODUCTS.
 */
export const ALL_CATEGORIES = Array.from(
  new Set(ALL_PRODUCTS.map((p) => p.category)),
).sort();

/**
 * Products for a given category — used to filter the Product dropdown
 * once the admin picks a Category.
 */
export const getProductsForCategory = (category) =>
  ALL_PRODUCTS.filter((p) => p.category === category).map((p) => p.product);
